/**
 * @fileoverview API Client
 * @description Fetch-based API client with JWT authentication
 * @module @xtream/frontend/services/api/client
 *
 * Features:
 * - JWT token management (auto-inject, auto-refresh)
 * - Request/response interceptors
 * - Error handling and normalization
 * - Request timeout support
 * - Content-type handling
 */

import { useAuthStore } from '@store/auth.store';
import type { IApiResponse, IApiError } from '@xtream/shared-types';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api';
const DEFAULT_TIMEOUT = 30_000; // 30 seconds

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Request configuration
 */
export interface RequestConfig extends Omit<RequestInit, 'body'> {
  /** Request body (will be JSON stringified if object) */
  body?: unknown;
  /** Skip authentication header */
  skipAuth?: boolean;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Query parameters */
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * API error with standardized format
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details: Record<string, unknown> | undefined;

  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  static fromResponse(error: IApiError, status: number): ApiError {
    return new ApiError(error.message, error.code, status, error.details);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// TOKEN REFRESH
// ═══════════════════════════════════════════════════════════════════════════

let isRefreshing = false;
interface RefreshSubscriber {
  onSuccess: (token: string) => void;
  onError: (error: ApiError) => void;
}

let refreshSubscribers: RefreshSubscriber[] = [];

/**
 * Subscribe to token refresh
 */
function subscribeTokenRefresh(
  onSuccess: (token: string) => void,
  onError: (error: ApiError) => void,
): void {
  refreshSubscribers.push({ onSuccess, onError });
}

/**
 * Notify subscribers with new token
 */
function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((subscriber) => subscriber.onSuccess(token));
  refreshSubscribers = [];
}

/**
 * Notify subscribers that token refresh failed
 */
function onTokenRefreshFailed(error: ApiError): void {
  refreshSubscribers.forEach((subscriber) => subscriber.onError(error));
  refreshSubscribers = [];
}

/**
 * Attempt to refresh the access token
 */
async function refreshToken(): Promise<string | null> {
  const { refreshToken: storedRefreshToken, updateAccessToken, logout } = useAuthStore.getState();

  if (!storedRefreshToken) {
    logout();
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken: storedRefreshToken }),
    });

    if (!response.ok) {
      logout();
      return null;
    }

    const data = await response.json();

    if (data.success && data.data?.accessToken) {
      updateAccessToken(data.data.accessToken, data.data.expiresIn);
      return data.data.accessToken;
    }

    logout();
    return null;
  } catch {
    logout();
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// API CLIENT CLASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * API client for making authenticated HTTP requests
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Build full URL with query parameters
   */
  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): string {
    // Normalize endpoint to not start with / for proper concatenation
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    // Build the full path by concatenating baseUrl + endpoint
    const basePath = this.baseUrl.endsWith('/') ? this.baseUrl : `${this.baseUrl}/`;
    const fullPath = `${basePath}${normalizedEndpoint}`;

    // Create URL with the full path
    const url = new URL(
      fullPath,
      this.baseUrl.startsWith('http') ? undefined : window.location.origin,
    );

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Get authorization header
   */
  private getAuthHeader(): Record<string, string> {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      return { Authorization: `Bearer ${accessToken}` };
    }
    return {};
  }

  /**
   * Handle token refresh on 401 response
   */
  private async handleUnauthorized<T>(
    endpoint: string,
    config: RequestConfig,
  ): Promise<IApiResponse<T>> {
    if (isRefreshing) {
      // Wait for ongoing refresh to complete
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(
          async (newToken) => {
            try {
              const retryConfig = {
                ...config,
                headers: {
                  ...config.headers,
                  Authorization: `Bearer ${newToken}`,
                },
              };
              const result = await this.request<T>(endpoint, retryConfig);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          },
          (error) => {
            reject(error);
          },
        );
      });
    }

    isRefreshing = true;
    let refreshSucceeded = false;

    try {
      const newToken = await refreshToken();

      if (newToken) {
        onTokenRefreshed(newToken);
        refreshSucceeded = true;

        // Retry the original request
        return this.request<T>(endpoint, config);
      }

      const sessionExpiredError = new ApiError('Session expired', 'UNAUTHORIZED', 401);
      onTokenRefreshFailed(sessionExpiredError);
      throw sessionExpiredError;
    } catch (error) {
      const normalizedError =
        error instanceof ApiError
          ? error
          : new ApiError(
              error instanceof Error ? error.message : 'Failed to refresh session',
              'UNAUTHORIZED',
              401,
            );

      if (!refreshSucceeded) {
        onTokenRefreshFailed(normalizedError);
      }

      throw normalizedError;
    } finally {
      isRefreshing = false;
    }
  }

  /**
   * Make an HTTP request
   */
  async request<T = unknown>(
    endpoint: string,
    config: RequestConfig = {},
  ): Promise<IApiResponse<T>> {
    const {
      body,
      skipAuth = false,
      timeout = DEFAULT_TIMEOUT,
      params,
      headers: customHeaders,
      ...restConfig
    } = config;

    const url = this.buildUrl(endpoint, params);

    // Build headers
    const authHeader = !skipAuth ? this.getAuthHeader() : {};
    const headers: HeadersInit = {
      Accept: 'application/json',
      ...authHeader,
      ...customHeaders,
    };

    // Only add Content-Type for requests with a body
    if (body !== undefined) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    // Build request config
    const requestConfig: RequestInit = {
      ...restConfig,
      headers,
    };

    // Only add body if provided
    if (body !== undefined) {
      requestConfig.body = JSON.stringify(body);
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestConfig.signal = controller.signal;

    try {
      const response = await fetch(url, requestConfig);
      clearTimeout(timeoutId);

      // Handle unauthorized (token refresh)
      if (response.status === 401 && !skipAuth) {
        return this.handleUnauthorized<T>(endpoint, config);
      }

      // Parse response - handle empty responses
      let responseData: IApiResponse<T>;
      const contentType = response.headers.get('content-type');
      const hasJsonContent = contentType?.includes('application/json');

      try {
        const text = await response.text();
        if (text && hasJsonContent) {
          responseData = JSON.parse(text);
        } else if (text) {
          // Non-JSON response
          throw new ApiError(
            `Server returned non-JSON response: ${text.substring(0, 100)}`,
            'INVALID_RESPONSE',
            response.status,
          );
        } else {
          // Empty response - might be OK for some endpoints
          responseData = { success: response.ok, data: undefined as T };
        }
      } catch (parseError) {
        if (parseError instanceof ApiError) {
          throw parseError;
        }
        throw new ApiError(
          'Failed to parse server response. Is the backend running?',
          'PARSE_ERROR',
          response.status,
        );
      }

      // Handle error responses
      if (!response.ok) {
        if (responseData.error) {
          throw ApiError.fromResponse(responseData.error, response.status);
        }
        throw new ApiError(
          `Request failed with status ${response.status}`,
          'REQUEST_FAILED',
          response.status,
        );
      }

      return responseData;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('Request timed out', 'TIMEOUT', 408);
      }

      // Re-throw ApiError
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors (backend not running)
      if (error instanceof TypeError) {
        throw new ApiError('Cannot connect to server. Is the backend running?', 'NETWORK_ERROR', 0);
      }

      // Unknown error
      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        'UNKNOWN_ERROR',
        500,
      );
    }
  }

  /**
   * GET request
   */
  async get<T = unknown>(
    endpoint: string,
    config?: Omit<RequestConfig, 'method' | 'body'>,
  ): Promise<IApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: Omit<RequestConfig, 'method'>,
  ): Promise<IApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: Omit<RequestConfig, 'method'>,
  ): Promise<IApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: Omit<RequestConfig, 'method'>,
  ): Promise<IApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(
    endpoint: string,
    config?: Omit<RequestConfig, 'method' | 'body'>,
  ): Promise<IApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Default API client instance
 */
export const apiClient = new ApiClient(API_BASE_URL);

export { ApiClient };
export default apiClient;
